import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, makeStyles, FormGroup } from "@material-ui/core";
import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import InputAutocompleteAddress from "components/Input/InputAutocompleteAddress";
import CustomDatePicker from "components/Input/CustomDatePicker";
import { updatePersonalInfo, fetchWorkerDetail } from "store/slices/worker";
import moment from "moment";
import AppColor from "utilities/AppColor";
import { YupValidations } from "utils/validate";
import SelectField from "components/Select/SelectField";
import { WORK_STATUS } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 15,
    marginBottom: 16,
  },
  documentBox: {
    background: "#F3F8F8",
    padding: "10px",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    marginBottom: 16,
    width: "100%",
  },
  documentHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: '50%',
    display: 'flex',
    alignItems: 'center',
    paddingRight: '5px',
    // marginBottom: 15,
  },
  documentIcon: {
    fontSize: 22,
    color: "#0D9488",
  },
  documentName: {
    fontSize: 14,
    fontWeight: 700,
    color: "#111827",
  },
  documentGrid: {
    // display: "grid",
    // gridTemplateColumns: "1fr 1fr 1fr",
    width: '50%',
    gap: 20,
  },
  docItem: {
    display: "flex",
    // flexDirection: "column",
    gap: 4,
  },
  docLabel: {
    fontSize: 11,
    color: "#6B7280",
  },
  docValue: {
    fontSize: 12,
    fontWeight: 500,
    color: "#111827",
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
    marginBottom: 20
  }
}));

const PersonalTab = ({ staffDetail, onChange, isCreate }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [latLng, setLatLng] = useState({});

  // Address validation state
  const [validAddress, setValidAddress] = useState(true);

  // Ref to hold validateForm from Formik render prop
  const validateFormRef = useRef(null);

  // Handle address change (user types without selecting suggestion)
  const handleAddressChange = (value = '') => {
    setValidAddress(value.trim() === staffDetail?.address1);
  };


  const {
    id = null,
    firstName = "",
    lastName = "",
    email = "",
    phone = "",
    dateOfBirth = "",
    gender = "",
    address1 = "",
    postalCode = "",
    workStatus = "",
    lat,
    lng,
  } = staffDetail || {};

  const emergencyContactName = staffDetail?.emergencyContactName ? staffDetail?.emergencyContactName : staffDetail?.references?.[0]?.name || "";
  const emergencyContactNumber = staffDetail?.emergencyContactNumber ? staffDetail?.emergencyContactNumber : staffDetail?.references?.[0]?.telephoneNumber || "";

  useEffect(() => {
    if (lat && lng) {
      setLatLng({ lat, lng });
    }
  }, [lat, lng]);

  // Reset address validation when staffDetail changes
  useEffect(() => {
    setValidAddress(true);
  }, [address1]);

  // Force Formik re-validation when validAddress changes
  // This is the KEY fix: Yup .test() only runs when Formik validates,
  // so we must manually trigger validation when external state changes
  useEffect(() => {
    if (validateFormRef.current) {
      validateFormRef.current();
    }
  }, [validAddress]);

  const validationSchema = Yup.object().shape({
    firstName: YupValidations.requiredString.max(35, "validation.max_length_35"),
    lastName: YupValidations.requiredString.max(35, "validation.max_length_35"),
    email: Yup.string().trim()
      .required('validation.email.required')
      .email('validation.email.invalid')
      .max(255, { message: 'validation.max_length', value: 255 }),
    phone: YupValidations.phone,
    address1: YupValidations.requiredString.test(
      'address-suggestion-required',
      'validation.address.must_select_suggestion',
      () => validAddress
    ),
    postalCode: YupValidations.postalCode,
    emergencyContactName: YupValidations.requiredString,
    emergencyContactNumber: YupValidations.phone,
    workStatus: YupValidations.requiredString,
    gender: YupValidations.requiredString,
    dateOfBirth: YupValidations.requiredString,
  });

  const initForm = {
    firstName,
    lastName,
    email,
    phone,
    dateOfBirth,
    gender,
    address1,
    postalCode,
    emergencyContactName,
    emergencyContactNumber,
    workStatus,
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      id,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      dateOfBirth: values.dateOfBirth,
      gender: values.gender,
      address1: values.address1,
      postalCode: values.postalCode,
      emergencyContactName: values.emergencyContactName,
      emergencyContactNumber: values.emergencyContactNumber,
      workStatus: values.workStatus,
      lat: latLng.lat,
      lng: latLng.lng
    };

    const result = await dispatch(updatePersonalInfo({ contentData: payload }));
    if (updatePersonalInfo.fulfilled.match(result)) {
      if (onChange && id) {
        // Refresh staff detail
        const freshDetail = await dispatch(fetchWorkerDetail({ workerId: id }));
        if (freshDetail.payload) {
          onChange(freshDetail.payload);
        }
      }
    }
    setSubmitting(false);
  };

  const rightToWorkDocs = [
    { key: "workerNdisWorkerScreeningCheckResponseList", title: "workerDocument.workerNdisWorkerScreeningCheckResponseList", icon: "fas fa-shield-alt" },
    { key: "workerRightToWorkInAustraliaResponseList", title: "workerDocument.workerRightToWorkInAustraliaResponseList", icon: "fas fa-passport" },
    { key: "workerWorkingChildrenDocResponseList", title: "workerDocument.workerWorkingChildrenDocResponseList", icon: "fas fa-child" },
    { key: "workerComprehensiveInsuranceResponseList", title: "workerDocument.workerComprehensiveInsuranceResponseList", icon: "fas fa-car-crash" },
    { key: "workerCarRegistrationResponseList", title: "workerDocument.workerCarRegistrationResponseList", icon: "fas fa-car" },
    { key: "workerCprDocResponseList", title: "workerDocument.workerCprDocResponseList", icon: "fas fa-heartbeat" },
    { key: "workerAgreementFileResponseList", title: "workerDocument.workerAgreementFileResponseList", icon: "fas fa-file-contract" },
    { key: "workerResumeResponseList", title: "workerDocument.workerResumeResponseList", icon: "fas fa-file-alt" },
    { key: "workerPoliceCheckFileResponseList", title: "workerDocument.workerPoliceCheckFileResponseList", icon: "fas fa-file" },
    { key: "workerCertificationFileResponseList", title: "workerDocument.workerCertificationFileResponseList", icon: "fas fa-graduation-cap" },
  ];

  const workStatusOptions = [
    { value: WORK_STATUS.AUSTRALIAN_CITIZEN, label: t(`workStatus.${WORK_STATUS.AUSTRALIAN_CITIZEN}`) },
    { value: WORK_STATUS.AUSTRALIA_WORK_VISA, label: t(`workStatus.${WORK_STATUS.AUSTRALIA_WORK_VISA}`) },
    { value: WORK_STATUS.NEW_ZEALAND_CITIZEN, label: t(`workStatus.${WORK_STATUS.NEW_ZEALAND_CITIZEN}`) },
  ];

  const genderOptions = [
    { value: "FEMALE", label: t("label.female") },
    { value: "MALE", label: t("label.male") },
    { value: "OTHER", label: t("label.other") },
  ];

  return (
    <Box>
      <Formik
        enableReinitialize={true}
        initialValues={initForm}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, validateForm }) => {
          // Store validateForm in ref so useEffect can call it
          validateFormRef.current = validateForm;
          return (
          <Form>
            <Typography className={classes.sectionTitle}>
              {t("title.personalInformation")}
            </Typography>

            <div className="row">
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="firstName"
                  label={t("label.firstName")}
                  required
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="lastName"
                  label={t("label.lastName")}
                  required
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="email"
                  label={t("label.email")}
                  required
                  disabled
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="phone"
                  label={t("label.phone")}
                  required
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={CustomDatePicker}
                  required
                  name="dateOfBirth"
                  type="text"
                  id="dateOfBirth"
                  label={"label.dateOfBirth"}
                  classInput={'date-picker-container-custom'}
                  formatDateJob={true}
                  custom={{
                    openTo: "year",
                    views: ["year", "month", "date"],
                    maxDate: new Date(moment(new Date().setDate(new Date().getDate() - 1)))
                  }}
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={SelectField}
                  name="gender"
                  label={"label.gender"}
                  options={genderOptions}
                  placeholder={t("label.gender")}
                  required={!isCreate}
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={InputAutocompleteAddress}
                  name="address1"
                  label={t("label.address")}
                  onChangePostalCode={(postalcode) => {
                    setFieldValue("postalCode", postalcode);
                  }}
                  onChangeLatLng={(latlng) => {
                    setValidAddress(true);
                    setLatLng(latlng)}
                  }
                  setValidAddress={setValidAddress}
                  allowPattern={true}
                  onAddressChange={handleAddressChange}
                  required
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="postalCode"
                  label={t("label.postalCode")}
                  required
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="emergencyContactName"
                  required
                  label={t("label.emergencyContactName")}
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <Field
                  component={AppInput}
                  name="emergencyContactNumber"
                  required
                  label={t("label.emergencyContactNumber")}
                />
              </div>
            </div>

            <Box>
              <Typography className={classes.sectionTitle}>
                {t("label.rightToWork")}
              </Typography>

              <Box style={{ marginBottom: 20 }}>
                <Field
                  component={SelectField}
                  required
                  name="workStatus"
                  label={"label.citizenshipStatus"}
                  options={workStatusOptions}
                  placeholder={t("label.citizenshipStatus")}
                // disabled={disabled}
                // required={isUpdate}
                />
              </Box>

              {rightToWorkDocs.map((docType) => {
                const list = staffDetail ? staffDetail[docType.key] : [];
                // if (!list || list.length === 0) return null;
                const item = list?.[0] || {};

                return (
                  <Box className={classes.documentBox} key={docType.key} display={'flex'}>
                    <Box className={classes.documentHeader}>
                      <i className={`${docType.icon} ${classes.documentIcon}`}></i>
                      <Typography className={classes.documentName}>
                        {t(docType.title)}
                      </Typography>
                    </Box>
                    <Box className={classes.documentGrid}>
                      <Box className={classes.docItem}>
                        <Box className={classes.docLabel}>{t("label.sightedBy")}:</Box>
                        <Box className={classes.docValue}>
                          {item.role} — {item.firstName} {item.lastName}
                        </Box>
                      </Box>
                      <Box className={classes.docItem}>
                        <Box className={classes.docLabel}>{t("label.dateSighted")}:</Box>
                        <Box className={classes.docValue}>
                          {item.updatedDate ? moment(item.updatedDate).format('DD/MM/YYYY') :
                            (item.createdDate ? moment(item.createdDate).format('DD/MM/YYYY') : "—")}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>


            <FormGroup className={classes.control}>
              <AppButton
                label="Cancel"
                onClick={() => history.push("/admin/workers")}
                buttonColor="#F3F4F6"
                rounder
                isLoading={isSubmitting}
                disabled={isSubmitting}
                style={{ color: "#111827", marginRight: 8 }}
              />
              <AppButton
                label={t("button.save_changes")}
                rounder
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                buttonColor={AppColor.mainColor}
              />
            </FormGroup>
          </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

PersonalTab.propTypes = {
  staffDetail: PropTypes.object,
  onChange: PropTypes.func,
};

export default PersonalTab;
